import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { CircleHalf} from "phosphor-react";
import { useI18n } from "../../i18n/useI18n";
import { THEME_STORAGE_KEY, ThemeModes } from "./constants";
import {
  applyTheme,
  createSystemThemeObserver,
  normalizeThemeMode,
  setThemePreference,
  readStoredTheme
} from "./themeUtils";
import css from "./themeModeToggle.less";
const [HbrMenuItem, HbrViewSwitcher, HbrRadioButton] = reactWrapper([
  "hbr-menu-item",
  "hbr-view-switcher",
  "hbr-radio-button",
]);

/**
 * Theme options with their icons
 * Defined outside component to prevent recreation
 */
const THEME_OPTIONS = [
  { mode: ThemeModes.LIGHT, labelKey: "themeModeLight" },
  { mode: ThemeModes.DARK, labelKey: "themeModeDark" },
  { mode: ThemeModes.SYSTEM, labelKey: "themeModeSystem" }
];

/**
 * Gets the initial theme from storage or defaults to light
 */
const getInitialTheme = () => {
  const stored = readStoredTheme(THEME_STORAGE_KEY);
  return normalizeThemeMode(stored);
};

const ThemeModeToggleComponent = () => {
  const { tGenerics } = useI18n("generics");
  const [currentTheme, setCurrentTheme] = useState(getInitialTheme);

  /**
   * Handle theme change - validates, updates state, and persists
   */
  const handleThemeChange = useCallback((event) => {
    const selectedTheme =
      event?.detail?.value || event?.target?.value || event?.currentTarget?.value;
    const normalizedTheme = normalizeThemeMode(selectedTheme);
    setCurrentTheme(normalizedTheme);
    setThemePreference(THEME_STORAGE_KEY, normalizedTheme);
  }, []);

  /**
   * Apply theme to DOM whenever currentTheme changes
   */
  useEffect(() => {
    applyTheme(currentTheme);

    if (currentTheme !== ThemeModes.SYSTEM) {
      return undefined;
    }

    const observer = createSystemThemeObserver(() => {
      applyTheme(ThemeModes.SYSTEM);
    });

    return () => observer.disconnect();
  }, [currentTheme]);

  /**
   * Theme options with translated labels
   */
  const themes = useMemo(
    () => THEME_OPTIONS.map(({ mode, labelKey }) => ({
      mode,
      label: tGenerics(labelKey)
    })),
    [tGenerics]
  );

  return (
    <HbrMenuItem
      key="theme"
      noninteractive={true} 
      data-testid="theme-mode-switcher"
    >
      <CircleHalf className={css["theme-mode-icon"]} slot="prefix" weight="fill" size={16}/> 
      <div className={css["theme-menu-item-label"]}>{tGenerics('theme')}</div>
      <HbrViewSwitcher
        slot="suffix"
        name="theme"
        value={currentTheme}
        onHbr-change={handleThemeChange}
        data-testid="theme-mode-view-switcher"
      >
        {themes.map(({ mode, label }) => (
          <HbrRadioButton key={mode} value={mode} size="small" data-testid={`theme-mode-radio-${mode}`}>
            {label}
          </HbrRadioButton>
        ))}
      </HbrViewSwitcher>
    </HbrMenuItem>
  );
};

/**
 * Theme mode toggle component
 * Provides a dropdown to switch between light and dark themes
 * Persists user preference to localStorage
 */
const ThemeModeToggle = memo(ThemeModeToggleComponent);
ThemeModeToggle.displayName = "ThemeModeToggle";

export default ThemeModeToggle;
export { ThemeModes };
